import {
  TeamPerformanceInsightsRangePreset,
  TeamPerformanceInsightsMetricsInput,
} from '../../../generated/graphql';
import {
  retrieveUserProviderItem,
  storeUserProviderItem,
} from '../../lib/storage';

const STORAGE_KEY = 'insightsFilters';

export type InsightsFilterState = {
  preset: TeamPerformanceInsightsRangePreset;
  /** YYYY-MM-DD, provider-local; used only for CUSTOM */
  startDate: string | null;
  /** YYYY-MM-DD, provider-local, inclusive; used only for CUSTOM */
  endDate: string | null;
  providerServiceIds: string[];
  includeTagIds: string[];
  excludeTagIds: string[];
};

export const DEFAULT_INSIGHTS_FILTERS: InsightsFilterState = {
  preset: TeamPerformanceInsightsRangePreset.CalendarMonth,
  startDate: null,
  endDate: null,
  providerServiceIds: [],
  includeTagIds: [],
  excludeTagIds: [],
};

const isStringArray = (value: unknown): value is string[] =>
  Array.isArray(value) && value.every((v) => typeof v === 'string');

const isPreset = (
  value: unknown,
): value is TeamPerformanceInsightsRangePreset =>
  Object.values(TeamPerformanceInsightsRangePreset).includes(
    value as TeamPerformanceInsightsRangePreset,
  );

/** Drop ids that no longer exist; keep everything while the known list is still loading (null). */
const prune = (value: unknown, known: Set<string> | null): string[] => {
  const ids = isStringArray(value) ? value : [];
  return known ? ids.filter((id) => known.has(id)) : ids;
};

/** Stored per user and provider: tag and service ids only exist within one provider. */
export const loadInsightsFilters = (
  userId: string,
  providerId: string,
  knownTagIds: Set<string> | null,
  knownServiceIds: Set<string> | null,
): InsightsFilterState => {
  const stored = retrieveUserProviderItem<Partial<InsightsFilterState>>(
    userId,
    providerId,
    STORAGE_KEY,
  );
  if (!stored || !isPreset(stored.preset)) {
    return DEFAULT_INSIGHTS_FILTERS;
  }
  return {
    preset: stored.preset,
    startDate: typeof stored.startDate === 'string' ? stored.startDate : null,
    endDate: typeof stored.endDate === 'string' ? stored.endDate : null,
    providerServiceIds: prune(stored.providerServiceIds, knownServiceIds),
    includeTagIds: prune(stored.includeTagIds, knownTagIds),
    excludeTagIds: prune(stored.excludeTagIds, knownTagIds),
  };
};

/** Drop ids missing from each list that has loaded; a list that hasn't loaded (null) keeps its ids. */
export const pruneFilters = (
  state: InsightsFilterState,
  knownTagIds: Set<string> | null,
  knownServiceIds: Set<string> | null,
): InsightsFilterState => ({
  ...state,
  providerServiceIds: prune(state.providerServiceIds, knownServiceIds),
  includeTagIds: prune(state.includeTagIds, knownTagIds),
  excludeTagIds: prune(state.excludeTagIds, knownTagIds),
});

export type FilterList = 'tags' | 'services';

/**
 * Lists that failed to load while the filter still uses their ids. Their ids can't be verified,
 * and dropping them would silently show totals for a different filter, so the query waits.
 */
export const filtersBlockedByFailedLists = (
  state: InsightsFilterState,
  {
    tagsFailed,
    servicesFailed,
  }: { tagsFailed: boolean; servicesFailed: boolean },
): FilterList[] => {
  const blocked: FilterList[] = [];
  if (
    tagsFailed &&
    (state.includeTagIds.length > 0 || state.excludeTagIds.length > 0)
  ) {
    blocked.push('tags');
  }
  if (servicesFailed && state.providerServiceIds.length > 0) {
    blocked.push('services');
  }
  return blocked;
};

export type CustomRangeProblem = 'incomplete' | 'reversed' | null;

export const customRangeProblem = (
  state: InsightsFilterState,
): CustomRangeProblem => {
  if (state.preset !== TeamPerformanceInsightsRangePreset.Custom) {
    return null;
  }
  if (!state.startDate || !state.endDate) {
    return 'incomplete';
  }
  // YYYY-MM-DD strings compare correctly as text
  return state.startDate > state.endDate ? 'reversed' : null;
};

/** Saves the filter unless it is one the server would reject (a reversed custom range). */
export const saveInsightsFilters = (
  userId: string,
  providerId: string,
  state: InsightsFilterState,
): void => {
  if (customRangeProblem(state) === 'reversed') {
    return;
  }
  storeUserProviderItem(userId, providerId, STORAGE_KEY, state);
};

/**
 * Skip until tags and services have settled (loaded or failed), so persisted ids are pruned
 * before the first request, and while the custom range is incomplete or reversed.
 */
export const shouldSkipMetricsQuery = ({
  canAccess,
  filters,
  listsSettled,
  blockedByFailedLists,
}: {
  canAccess: boolean;
  filters: InsightsFilterState;
  listsSettled: boolean;
  blockedByFailedLists: boolean;
}): boolean =>
  !canAccess ||
  !listsSettled ||
  blockedByFailedLists ||
  customRangeProblem(filters) !== null;

/** Keep the last numbers visible while refetching, but never under an error for a different filter. */
export const displayedMetrics = <T>(
  data: T | undefined,
  previousData: T | undefined,
  hasError: boolean,
): T | undefined => {
  if (data !== undefined) {
    return data;
  }
  return hasError ? undefined : previousData;
};

export const toQueryInput = (
  state: InsightsFilterState,
  browserTz: string,
): TeamPerformanceInsightsMetricsInput => {
  const isCustom = state.preset === TeamPerformanceInsightsRangePreset.Custom;
  return {
    preset: state.preset,
    ...(isCustom ? { startDate: state.startDate, endDate: state.endDate } : {}),
    timezone: browserTz,
    providerServiceIds: state.providerServiceIds,
    includeTagIds: state.includeTagIds,
    excludeTagIds: state.excludeTagIds,
  };
};

/** Caption for the range the server actually counted; a null start means all time. */
export const formatRangeCaption = (
  rangeStart: string | null | undefined,
  rangeEnd: string,
  timezone: string,
): string => {
  if (!rangeStart) {
    return 'All time';
  }
  const start = new Date(rangeStart);
  // rangeEnd is exclusive: label the day of the last included moment
  const lastIncluded = new Date(new Date(rangeEnd).getTime() - 1);
  const yearOf = (date: Date) =>
    new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      timeZone: timezone,
    }).format(date);
  const day = (date: Date, withYear: boolean) =>
    new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      ...(withYear ? { year: 'numeric' } : {}),
      timeZone: timezone,
    }).format(date);
  const sameYear = yearOf(start) === yearOf(lastIncluded);
  return `${day(start, !sameYear)} – ${day(lastIncluded, true)} · ${timezone}`;
};

export type ClientsDateRange = { startDate: string; endDate: string };

/**
 * Window for the per-client tables: the range the server counted, with all time starting at the epoch.
 * The metrics `rangeEnd` is exclusive but the client-summary query includes `endDate`, so it ends 1 ms earlier.
 */
export const toClientsDateRange = (
  rangeStart: string | null | undefined,
  rangeEnd: string,
): ClientsDateRange => ({
  startDate: rangeStart ?? new Date(0).toISOString(),
  endDate: new Date(new Date(rangeEnd).getTime() - 1).toISOString(),
});

type TaggedClient = { tags: { id: string }[] };

/** Same tag rule as the metrics: keep clients with any included tag (if any are set), drop any with an excluded tag. */
export const filterClientsByTags = <T extends TaggedClient>(
  clients: T[],
  includeTagIds: string[],
  excludeTagIds: string[],
): T[] =>
  clients.filter((client) => {
    const ids = client.tags.map((tag) => tag.id);
    if (excludeTagIds.some((id) => ids.includes(id))) {
      return false;
    }
    return (
      includeTagIds.length === 0 || includeTagIds.some((id) => ids.includes(id))
    );
  });
